import { DEFAULT_LANG, LANG_OPTIONS } from './types';

/**
 * Custom-built i18n. No library because the string set is small (~70 keys)
 * and pulling in react-i18next would add ~10 KB gzipped + a peer-dep tree
 * for very little we'd actually use. The translations live below as plain
 * objects keyed by Tesseract language code so the existing `lang` state
 * (which already drives receipt-OCR language picking) drives UI strings
 * too — one picker, one localStorage key, one source of truth.
 *
 * Plurals: simple "is the count 1?" branches, with separate `.one` / `.many`
 * keys. Good enough for the 7 Indo-European languages we ship; if we ever
 * add Russian / Polish / Arabic with their multi-form pluralization we'll
 * lift to a real ICU library.
 *
 * Interpolation: `{name}` placeholders, replaced via a tiny string sub.
 *
 * Auto-detect: `detectInitialUiLang` reads `navigator.language` (e.g.
 * "es-ES", "fr-CA"), maps the 2-letter prefix to one of our language
 * codes, falls back to English. Caller persists the chosen value to
 * localStorage; the existing `rr-lang` key is reused so a user who
 * already had a language saved keeps their pick.
 */

export type UiLangCode = (typeof LANG_OPTIONS)[number]['code'];

interface Strings {
  // Header / footer
  'tagline.onDevice': string;
  'footer.dataPrivacy': string;
  'footer.licenses': string;
  'footer.about': string;
  'footer.faq': string;
  'footer.guides': string;
  'footer.contact': string;
  'footer.privacy': string;
  'footer.terms': string;
  'nav.home': string;

  // Language picker
  'language.label': string;
  'language.firstUseDownload': string;
  'language.receiptLabel': string;
  'receiptLang.title': string;
  'receiptLang.body': string;
  'receiptLang.confirmSame': string;
  'receiptLang.chooseOther': string;
  'receiptLang.start': string;
  'receiptLang.cardLabel': string;

  // Dropzone
  'dropzone.dropHere': string;
  'dropzone.dropToUpload': string;
  'dropzone.subtitle': string;
  'dropzone.chooseFile': string;
  'dropzone.chooseFileAria': string;
  'dropzone.takePhoto': string;
  'dropzone.takePhotoAria': string;
  'dropzone.accepted': string;
  'dropzone.unsupported.one': string;
  'dropzone.unsupported.many': string;
  'dropzone.skipped.one': string;
  'dropzone.skipped.many': string;
  'dropzone.regionLabel': string;

  // Mobile post-batch quick-action
  'scan.another': string;

  // Sessions list
  'sessions.empty': string;
  'sessions.search.placeholder': string;
  'sessions.search.empty': string;
  'sessions.count.partial.one': string;
  'sessions.count.partial.many': string;
  'sessions.trySample': string;
  'sessions.count.one': string;
  'sessions.count.many': string;
  'sessions.clearDone': string;
  'sessions.clearDoneTooltip': string;
  'sessions.clearAll': string;
  'sessions.clearAllTooltip.inFlight': string;
  'sessions.clearAllTooltip.idle': string;
  'sessions.removeFromList': string;
  'sessions.removeAriaLabel': string;
  'sessions.lowQuality.title': string;
  'sessions.lowQuality.body': string;
  'sessions.recropManually': string;
  'sessions.recropTooltip': string;
  'sessions.rotate': string;
  'sessions.rotateTooltip': string;
  'sessions.duplicate': string;
  'sessions.duplicateTooltip': string;
  'sessions.dragHandle': string;
  'sessions.dragHandleAria': string;
  'sessions.previewTooltip': string;
  'sessions.previewAria': string;
  'sessions.cropPreview.hint': string;

  // States / stages
  'state.queued': string;
  'state.done': string;
  'state.review': string;
  'sessions.confirmScan': string;
  'state.error': string;
  'state.lowQuality': string;
  'stage.load': string;
  'stage.preprocess': string;
  'stage.ocr': string;
  'stage.parse': string;
  'stage.validate': string;
  'stage.done': string;

  // Export menu
  'export.empty': string;
  'export.one': string;
  'export.many': string;
  'export.csv': string;
  'export.xlsx': string;
  'export.zip': string;
  'export.zipTooltip': string;
  'export.billCsv': string;
  'export.billCsvTooltip': string;
  'taxRate.label': string;
  'taxRate.placeholder': string;
  'taxRate.tooltip': string;
  'taxRate.applyAll': string;
  'taxRate.applyAllTooltip': string;
  'export.regionLabel': string;

  // Batch progress
  'batch.processing': string;
  'batch.allDone.one': string;
  'batch.allDone.many': string;
  'batch.allDoneWithErrors.one': string;
  'batch.allDoneWithErrors.many': string;
  'batch.grandTotal': string;
  'batch.partial': string;

  // Receipt table
  'receipt.heading': string;
  'receipt.lineItems': string;
  'receipt.empty': string;
  'receipt.noItems': string;
  'receipt.fields.vendor': string;
  'receipt.fields.date': string;
  'receipt.fields.currency': string;
  'receipt.fields.subtotal': string;
  'receipt.fields.tax': string;
  'receipt.fields.tip': string;
  'receipt.fields.total': string;
  'receipt.fields.payment': string;
  'receipt.cols.item': string;
  'receipt.cols.qty': string;
  'receipt.cols.unitPrice': string;
  'receipt.cols.total': string;
  'receipt.addItem': string;
  'receipt.removeItem': string;
  'receipt.dragHandle': string;
  'receipt.dragHandleAria': string;

  // Crop modal
  'crop.title': string;
  'crop.description': string;
  'crop.cancel': string;
  'crop.apply': string;
  'crop.close': string;
  'crop.cornerAria': string;
  'crop.tapBottomLeft': string;
  'crop.tapTopLeft': string;
  'crop.tapTopRight': string;
  'crop.tapBottomRight': string;
  'crop.reset': string;

  // Confidence
  'confidence.high': string;
  'confidence.medium': string;
  'confidence.low': string;

  // Theme toggle (aria)
  'theme.toLight': string;
  'theme.toDark': string;

  // Debug toggle
  'debug.show': string;

  // Persistence toggle
  'persistence.label': string;
  'persistence.tooltip': string;

  // Exclude-tax-from-exports toggle
  'excludeTax.label': string;
  'excludeTax.tooltip': string;

  // Cookie consent banner (first-visit GDPR opt-in)
  'consent.message': string;
  'consent.accept': string;
  'consent.reject': string;
  'consent.privacy': string;
  'consent.preferences': string;

  // Pre-export donation prompt (5-sec delay before CSV / XLSX / ZIP fires)
  'donate.title': string;
  'donate.body': string;
  'donate.cta': string;
  'donate.skip': string;
  'donate.countdown': string; // takes {seconds}
  'donate.starting': string;

  // Inline validation hints
  'validation.totals.title': string;
  'validation.totals.body': string;

  // Batch summary panel
  'summary.title': string;
  'summary.byCategory': string;
  'summary.topVendors': string;
  'summary.byMonth': string;

  // Tags + notes
  'tags.label': string;
  'tags.placeholder': string;
  'tags.removeAria': string;
  'notes.label': string;
  'notes.placeholder': string;

  // Category dropdown
  'category.label': string;
  'category.placeholder': string;
  'category.autoBadge': string;
  'category.autoTooltip': string;
  'category.food': string;
  'category.transport': string;
  'category.office': string;
  'category.healthcare': string;
  'category.travel': string;
  'category.utilities': string;
  'category.entertainment': string;
  'category.subscriptions': string;
  'category.personal': string;
  'category.other': string;
}

export type StringKey = keyof Strings;

const eng: Strings = {
  'tagline.onDevice': 'Your receipts never leave your device',
  'footer.dataPrivacy': 'Your receipt content never leaves this device.',
  'footer.licenses': 'Open source licenses',
  'footer.about': 'About',
  'footer.faq': 'FAQ',
  'footer.guides': 'Guides',
  'footer.contact': 'Contact',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'nav.home': 'Home',
  'language.label': 'Interface',
  'language.receiptLabel': 'Receipts',
  'receiptLang.title': 'What language are your receipts in?',
  'receiptLang.body': 'This picks the OCR model that reads your receipt — not the language of the interface.',
  'receiptLang.confirmSame': 'Yes, {language}',
  'receiptLang.chooseOther': 'No, another language',
  'receiptLang.start': 'Start scanning',
  'receiptLang.cardLabel': 'Receipt language',
  'language.firstUseDownload': 'First use of a new language downloads ~12 MB.',
  'dropzone.dropHere': 'Drop receipts here',
  'dropzone.dropToUpload': 'Drop to upload',
  'dropzone.subtitle': 'one or many — or paste from clipboard, or',
  'dropzone.chooseFile': 'Choose file',
  'dropzone.chooseFileAria': 'Choose receipt files',
  'dropzone.takePhoto': 'Take a photo',
  'dropzone.takePhotoAria': 'Capture a receipt with the camera',
  'dropzone.accepted': 'Accepted: {extensions}',
  'dropzone.unsupported.one': 'Unsupported file type. Accepted: {accepted}',
  'dropzone.unsupported.many': 'Unsupported file types. Accepted: {accepted}',
  'dropzone.skipped.one': 'Skipped: 1 unsupported file.',
  'dropzone.skipped.many': 'Skipped: {n} unsupported files.',
  'dropzone.regionLabel': 'Receipt file dropzone',
  'scan.another': 'Scan another',
  'sessions.empty': 'Drop one or more receipts to get started.',
  'sessions.search.placeholder': 'Search vendor, item, or filename',
  'sessions.search.empty': 'No receipts match your search.',
  'sessions.count.partial.one': '1 of {total}',
  'sessions.count.partial.many': '{n} of {total}',
  'sessions.trySample': 'Try with a sample receipt',
  'sessions.count.one': '1 receipt',
  'sessions.count.many': '{n} receipts',
  'sessions.clearDone': 'Clear done ({n})',
  'sessions.clearDoneTooltip':
    'Remove every completed (success or error) receipt from the list',
  'sessions.clearAll': 'Clear all',
  'sessions.clearAllTooltip.inFlight':
    'Cancel any in-flight work and remove every receipt',
  'sessions.clearAllTooltip.idle': 'Remove every receipt from the list',
  'sessions.removeFromList': 'Remove from list',
  'sessions.removeAriaLabel': 'Remove {filename} from list',
  'sessions.lowQuality.title':
    'Photo quality is too low to read this receipt reliably.',
  'sessions.lowQuality.body':
    'For better results, retake the photo with the receipt flat, well-lit, fully in frame, and free of glare or hand shadows on the paper. The fields below are still shown but may be incomplete or wrong — edit them manually if needed.',
  'sessions.recropManually': 'Re-crop manually',
  'sessions.recropTooltip':
    'Manually adjust the receipt corners and reprocess',
  'sessions.rotate': 'Rotate 90°',
  'sessions.rotateTooltip':
    'Rotate the receipt image 90° clockwise and reprocess',
  'sessions.duplicate': 'Possible duplicate',
  'sessions.duplicateTooltip':
    'Another receipt in this batch has the same vendor, date, and total',
  'sessions.dragHandle': 'Drag to reorder',
  'sessions.dragHandleAria': 'Drag to reorder, {filename}',
  'sessions.previewTooltip': 'View the scanned image',
  'sessions.previewAria': 'View scanned image, {filename}',
  'sessions.cropPreview.hint':
    'Auto-crop preview — if it looks wrong, re-crop it now or remove the receipt and retake the photo.',
  'state.queued': 'Queued',
  'state.done': 'Done',
  'state.review': 'Check crop',
  'sessions.confirmScan': 'Looks good — scan',
  'state.error': 'Error',
  'state.lowQuality': 'Low quality',
  'stage.load': 'Loading',
  'stage.preprocess': 'Preprocessing',
  'stage.ocr': 'Running OCR',
  'stage.parse': 'Extracting fields',
  'stage.validate': 'Validating',
  'stage.done': 'Done',
  'export.empty': 'No receipts ready',
  'export.one': 'Export 1 receipt',
  'export.many': 'Export all ({n} receipts)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Download every receipt as a ZIP: each image as vendor_Mon_YY.jpg (groups by vendor) plus a workbook with a summary sheet and per-receipt breakdowns',
  'export.billCsv': 'Bill CSV',
  'export.billCsvTooltip':
    'Download every receipt in the accounting bill-import layout: one row per line item, DD/MM/YYYY dates, mandatory * columns kept verbatim',
  'taxRate.label': 'VAT rate',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'VAT percentage for this receipt. Leave empty to derive it from the parsed tax. Used as *LineTaxCode in the bill CSV export.',
  'taxRate.applyAll': 'Apply to all',
  'taxRate.applyAllTooltip': 'Copy this VAT rate onto every other parsed receipt in the batch',
  'export.regionLabel': 'Export options',
  'batch.processing': 'Processing — {done} of {total} done',
  'batch.allDone.one': 'All 1 receipt done',
  'batch.allDone.many': 'All {total} receipts done',
  'batch.allDoneWithErrors.one': 'All 1 receipt done ({errors} error{s})',
  'batch.allDoneWithErrors.many':
    'All {total} receipts done ({errors} error{s})',
  'batch.grandTotal': 'Grand total',
  'batch.partial': '{done} of {total} done',
  'receipt.heading': 'Receipt',
  'receipt.lineItems': 'Line items',
  'receipt.empty': 'Drop a receipt to get started.',
  'receipt.noItems': 'No line items detected.',
  'receipt.fields.vendor': 'Vendor',
  'receipt.fields.date': 'Date',
  'receipt.fields.currency': 'Currency',
  'receipt.fields.subtotal': 'Subtotal',
  'receipt.fields.tax': 'Tax',
  'receipt.fields.tip': 'Tip',
  'receipt.fields.total': 'Total',
  'receipt.fields.payment': 'Payment',
  'receipt.cols.item': 'Item',
  'receipt.cols.qty': 'Qty',
  'receipt.cols.unitPrice': 'Unit price',
  'receipt.cols.total': 'Total',
  'receipt.addItem': 'Add line item',
  'receipt.removeItem': 'Remove item, row {n}',
  'receipt.dragHandle': 'Drag to reorder',
  'receipt.dragHandleAria': 'Drag to reorder, row {n}',
  'crop.title': 'Manual crop',
  'crop.description':
    'Tap the four corners of the receipt — bottom-left first, then clockwise. Drag any point to fine-tune, then Apply.',
  'crop.cancel': 'Cancel',
  'crop.apply': 'Apply crop',
  'crop.close': 'Close',
  'crop.cornerAria': 'Corner {n}',
  'crop.tapBottomLeft': 'Tap the bottom-left corner of the receipt',
  'crop.tapTopLeft': 'Now the top-left corner',
  'crop.tapTopRight': 'Now the top-right corner',
  'crop.tapBottomRight': 'Finally the bottom-right corner',
  'crop.reset': 'Start over',
  'confidence.high': 'high',
  'confidence.medium': 'medium',
  'confidence.low': 'low',
  'theme.toLight': 'Switch to light theme',
  'theme.toDark': 'Switch to dark theme',
  'debug.show': 'Show debug info',
  'persistence.label': 'Keep batch on this device',
  'persistence.tooltip':
    'Save processed receipts to this browser so they survive a refresh. Stored locally only — nothing leaves the device.',
  'excludeTax.label': 'Exclude tax from exports',
  'excludeTax.tooltip':
    'When on, VAT/IVA tax info is left out of CSV / Excel / ZIP exports. Useful in jurisdictions where exporting tax data is risky. Subtotal is also hidden because it would let you derive tax (total − subtotal). Tip is independent and stays.',
  'consent.message':
    'Your receipts never leave your device. We use cookies for analytics and ads — accept to support the site, or reject to skip them.',
  'consent.accept': 'Accept',
  'consent.reject': 'Reject',
  'consent.privacy': 'Privacy policy',
  'consent.preferences': 'Cookie preferences',
  'donate.title': 'Support Receipt Ripper',
  'donate.body': 'Free, ad-supported, and your receipts never leave your device. Donations help keep it that way.',
  'donate.cta': 'Donate via Stripe',
  'donate.skip': 'Skip',
  'donate.countdown': 'Your download starts in {seconds}s',
  'donate.starting': 'Starting your download…',
  'validation.totals.title': "Items + tax + tip don't match the total",
  'validation.totals.body':
    'Items + tax + tip = {expected}, but the total reads {actual} (off by {gap}). Double-check the item totals.',
  'summary.title': 'Batch summary',
  'summary.byCategory': 'By category',
  'summary.topVendors': 'Top vendors',
  'summary.byMonth': 'By month',
  'tags.label': 'Tags',
  'tags.placeholder': 'Add tag…',
  'tags.removeAria': 'Remove tag {tag}',
  'notes.label': 'Notes',
  'notes.placeholder': 'Notes about this receipt…',
  'category.label': 'Category',
  'category.placeholder': 'Uncategorised',
  'category.autoBadge': 'auto · {pct}%',
  'category.autoTooltip':
    'Detected automatically from vendor + line items ({pct}% confident). Pick a category to confirm or override.',
  'category.food': 'Food',
  'category.transport': 'Transport',
  'category.office': 'Office',
  'category.healthcare': 'Healthcare',
  'category.travel': 'Travel',
  'category.utilities': 'Utilities',
  'category.entertainment': 'Entertainment',
  'category.subscriptions': 'Subscriptions',
  'category.personal': 'Personal',
  'category.other': 'Other',
};

const spa: Strings = {
  'tagline.onDevice': 'Tus recibos nunca salen de tu dispositivo',
  'footer.dataPrivacy': 'El contenido de tus recibos nunca sale de este dispositivo.',
  'footer.licenses': 'Licencias de código abierto',
  'footer.about': 'Acerca de',
  'footer.faq': 'Preguntas frecuentes',
  'footer.guides': 'Guías',
  'footer.contact': 'Contacto',
  'footer.privacy': 'Privacidad',
  'footer.terms': 'Términos',
  'nav.home': 'Inicio',
  'language.label': 'Interfaz',
  'language.receiptLabel': 'Recibos',
  'receiptLang.title': '¿En qué idioma están tus recibos?',
  'receiptLang.body': 'Esto selecciona el modelo de OCR que lee tu recibo, no el idioma de la interfaz.',
  'receiptLang.confirmSame': 'Sí, {language}',
  'receiptLang.chooseOther': 'No, otro idioma',
  'receiptLang.start': 'Empezar a escanear',
  'receiptLang.cardLabel': 'Idioma del recibo',
  'language.firstUseDownload':
    'El primer uso de un nuevo idioma descarga ~12 MB.',
  'dropzone.dropHere': 'Suelta los recibos aquí',
  'dropzone.dropToUpload': 'Suelta para subir',
  'dropzone.subtitle': 'uno o varios — o pega desde el portapapeles, o',
  'dropzone.chooseFile': 'Elegir archivo',
  'dropzone.chooseFileAria': 'Elegir archivos de recibo',
  'dropzone.takePhoto': 'Hacer una foto',
  'dropzone.takePhotoAria': 'Capturar un recibo con la cámara',
  'dropzone.accepted': 'Aceptados: {extensions}',
  'dropzone.unsupported.one': 'Tipo de archivo no compatible. Aceptados: {accepted}',
  'dropzone.unsupported.many':
    'Tipos de archivo no compatibles. Aceptados: {accepted}',
  'dropzone.skipped.one': 'Omitido: 1 archivo no compatible.',
  'dropzone.skipped.many': 'Omitidos: {n} archivos no compatibles.',
  'dropzone.regionLabel': 'Zona de soltado de recibos',
  'scan.another': 'Escanear otro',
  'sessions.empty': 'Suelta uno o más recibos para empezar.',
  'sessions.search.placeholder': 'Buscar proveedor, artículo o archivo',
  'sessions.search.empty': 'Ningún recibo coincide con la búsqueda.',
  'sessions.count.partial.one': '1 de {total}',
  'sessions.count.partial.many': '{n} de {total}',
  'sessions.trySample': 'Probar con un recibo de ejemplo',
  'sessions.count.one': '1 recibo',
  'sessions.count.many': '{n} recibos',
  'sessions.clearDone': 'Borrar terminados ({n})',
  'sessions.clearDoneTooltip':
    'Eliminar de la lista todos los recibos terminados (éxito o error)',
  'sessions.clearAll': 'Borrar todo',
  'sessions.clearAllTooltip.inFlight':
    'Cancelar el trabajo en curso y eliminar todos los recibos',
  'sessions.clearAllTooltip.idle': 'Eliminar todos los recibos de la lista',
  'sessions.removeFromList': 'Eliminar de la lista',
  'sessions.removeAriaLabel': 'Eliminar {filename} de la lista',
  'sessions.lowQuality.title':
    'La calidad de la foto es demasiado baja para leer este recibo con fiabilidad.',
  'sessions.lowQuality.body':
    'Para mejores resultados, vuelve a tomar la foto con el recibo plano, bien iluminado, completamente encuadrado y sin reflejos ni sombras de la mano sobre el papel. Los campos siguen mostrándose pero pueden estar incompletos o incorrectos — edítalos manualmente si es necesario.',
  'sessions.recropManually': 'Recortar manualmente',
  'sessions.recropTooltip':
    'Ajustar manualmente las esquinas del recibo y reprocesar',
  'sessions.rotate': 'Rotar 90°',
  'sessions.rotateTooltip':
    'Rotar la imagen del recibo 90° en sentido horario y reprocesar',
  'sessions.duplicate': 'Posible duplicado',
  'sessions.duplicateTooltip':
    'Otro recibo en este lote tiene el mismo proveedor, fecha y total',
  'sessions.dragHandle': 'Arrastrar para reordenar',
  'sessions.dragHandleAria': 'Arrastrar para reordenar, {filename}',
  'sessions.previewTooltip': 'Ver la imagen escaneada',
  'sessions.previewAria': 'Ver imagen escaneada, {filename}',
  'sessions.cropPreview.hint':
    'Vista previa del recorte automático: si no se ve bien, recórtalo manualmente ahora o elimina el recibo y repite la foto.',
  'state.queued': 'En cola',
  'state.done': 'Listo',
  'state.review': 'Revisar recorte',
  'sessions.confirmScan': 'Se ve bien — escanear',
  'state.error': 'Error',
  'state.lowQuality': 'Calidad baja',
  'stage.load': 'Cargando',
  'stage.preprocess': 'Preprocesando',
  'stage.ocr': 'Ejecutando OCR',
  'stage.parse': 'Extrayendo campos',
  'stage.validate': 'Validando',
  'stage.done': 'Listo',
  'export.empty': 'Sin recibos listos',
  'export.one': 'Exportar 1 recibo',
  'export.many': 'Exportar todos ({n} recibos)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Descargar todos los recibos como ZIP: cada imagen como proveedor_Mes_AA.jpg (agrupadas por proveedor) más un libro con una hoja resumen y desgloses por recibo',
  'export.billCsv': 'CSV de facturas',
  'export.billCsvTooltip':
    'Descargar todos los recibos con el formato de importación de facturas: una fila por línea, fechas DD/MM/AAAA, columnas obligatorias * intactas',
  'taxRate.label': 'Tipo de IVA',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'Porcentaje de IVA de este recibo. Déjalo vacío para deducirlo del impuesto detectado. Se usa como *LineTaxCode en el CSV de facturas.',
  'taxRate.applyAll': 'Aplicar a todos',
  'taxRate.applyAllTooltip': 'Copiar este tipo de IVA a todos los demás recibos del lote',
  'export.regionLabel': 'Opciones de exportación',
  'batch.processing': 'Procesando — {done} de {total} listos',
  'batch.allDone.one': '1 recibo listo',
  'batch.allDone.many': 'Los {total} recibos están listos',
  'batch.allDoneWithErrors.one': '1 recibo listo ({errors} error{s})',
  'batch.allDoneWithErrors.many':
    'Los {total} recibos están listos ({errors} error{s})',
  'batch.grandTotal': 'Total general',
  'batch.partial': '{done} de {total} listos',
  'receipt.heading': 'Recibo',
  'receipt.lineItems': 'Líneas',
  'receipt.empty': 'Suelta un recibo para empezar.',
  'receipt.noItems': 'No se detectaron líneas.',
  'receipt.fields.vendor': 'Proveedor',
  'receipt.fields.date': 'Fecha',
  'receipt.fields.currency': 'Moneda',
  'receipt.fields.subtotal': 'Subtotal',
  'receipt.fields.tax': 'Impuesto',
  'receipt.fields.tip': 'Propina',
  'receipt.fields.total': 'Total',
  'receipt.fields.payment': 'Pago',
  'receipt.cols.item': 'Artículo',
  'receipt.cols.qty': 'Cant.',
  'receipt.cols.unitPrice': 'Precio unit.',
  'receipt.cols.total': 'Total',
  'receipt.addItem': 'Añadir línea',
  'receipt.removeItem': 'Eliminar artículo, fila {n}',
  'receipt.dragHandle': 'Arrastrar para reordenar',
  'receipt.dragHandleAria': 'Arrastrar para reordenar, fila {n}',
  'crop.title': 'Recorte manual',
  'crop.description':
    'Toca las cuatro esquinas del recibo: primero la inferior izquierda y luego en sentido horario. Arrastra cualquier punto para ajustarlo y pulsa Aplicar.',
  'crop.cancel': 'Cancelar',
  'crop.apply': 'Aplicar recorte',
  'crop.close': 'Cerrar',
  'crop.cornerAria': 'Esquina {n}',
  'crop.tapBottomLeft': 'Toca la esquina inferior izquierda del recibo',
  'crop.tapTopLeft': 'Ahora la esquina superior izquierda',
  'crop.tapTopRight': 'Ahora la esquina superior derecha',
  'crop.tapBottomRight': 'Por último, la esquina inferior derecha',
  'crop.reset': 'Empezar de nuevo',
  'confidence.high': 'alta',
  'confidence.medium': 'media',
  'confidence.low': 'baja',
  'theme.toLight': 'Cambiar al tema claro',
  'theme.toDark': 'Cambiar al tema oscuro',
  'debug.show': 'Mostrar info de depuración',
  'persistence.label': 'Guardar el lote en este dispositivo',
  'persistence.tooltip':
    'Guarda los recibos procesados en este navegador para que sobrevivan a una recarga. Solo almacenamiento local — nada sale del dispositivo.',
  'excludeTax.label': 'Excluir IVA de las exportaciones',
  'excludeTax.tooltip':
    'Cuando está activado, la información de IVA queda fuera de las exportaciones CSV / Excel / ZIP. Útil en jurisdicciones donde exportar datos fiscales es arriesgado. El subtotal también se oculta porque permitiría calcular el IVA (total − subtotal). La propina es independiente y se mantiene.',
  'consent.message':
    'Tus recibos no salen de tu dispositivo. Usamos cookies para analítica y publicidad — acepta para apoyar el sitio, o rechaza para saltártelas.',
  'consent.accept': 'Aceptar',
  'consent.reject': 'Rechazar',
  'consent.privacy': 'Política de privacidad',
  'consent.preferences': 'Preferencias de cookies',
  'donate.title': 'Apoya a Receipt Ripper',
  'donate.body': 'Gratis, financiado por anuncios, y tus recibos nunca salen de tu dispositivo. Las donaciones ayudan a que siga siendo así.',
  'donate.cta': 'Donar con Stripe',
  'donate.skip': 'Saltar',
  'donate.countdown': 'Tu descarga empieza en {seconds}s',
  'donate.starting': 'Comenzando tu descarga…',
  'validation.totals.title':
    'Los artículos + impuesto + propina no cuadran con el total',
  'validation.totals.body':
    'Artículos + impuesto + propina = {expected}, pero el total marca {actual} (diferencia {gap}). Comprueba los totales de los artículos.',
  'summary.title': 'Resumen del lote',
  'summary.byCategory': 'Por categoría',
  'summary.topVendors': 'Principales proveedores',
  'summary.byMonth': 'Por mes',
  'tags.label': 'Etiquetas',
  'tags.placeholder': 'Añadir etiqueta…',
  'tags.removeAria': 'Eliminar etiqueta {tag}',
  'notes.label': 'Notas',
  'notes.placeholder': 'Notas sobre este recibo…',
  'category.label': 'Categoría',
  'category.placeholder': 'Sin categoría',
  'category.autoBadge': 'auto · {pct}%',
  'category.autoTooltip':
    'Detectada automáticamente a partir del proveedor + las líneas ({pct}% de confianza). Elige una categoría para confirmar o sobrescribir.',
  'category.food': 'Comida',
  'category.transport': 'Transporte',
  'category.office': 'Oficina',
  'category.healthcare': 'Salud',
  'category.travel': 'Viajes',
  'category.utilities': 'Suministros',
  'category.entertainment': 'Ocio',
  'category.subscriptions': 'Suscripciones',
  'category.personal': 'Personal',
  'category.other': 'Otros',
};

const fra: Strings = {
  'tagline.onDevice': 'Vos reçus ne quittent jamais votre appareil',
  'footer.dataPrivacy': 'Le contenu de vos reçus ne quitte jamais cet appareil.',
  'footer.licenses': 'Licences open source',
  'footer.about': 'À propos',
  'footer.faq': 'FAQ',
  'footer.guides': 'Guides',
  'footer.contact': 'Contact',
  'footer.privacy': 'Confidentialité',
  'footer.terms': 'Conditions',
  'nav.home': 'Accueil',
  'language.label': 'Interface',
  'language.receiptLabel': 'Reçus',
  'receiptLang.title': 'Dans quelle langue sont vos reçus ?',
  'receiptLang.body': 'Cela sélectionne le modèle d’OCR qui lit votre reçu, pas la langue de l’interface.',
  'receiptLang.confirmSame': 'Oui, {language}',
  'receiptLang.chooseOther': 'Non, une autre langue',
  'receiptLang.start': 'Commencer la numérisation',
  'receiptLang.cardLabel': 'Langue du reçu',
  'language.firstUseDownload':
    'La première utilisation d’une nouvelle langue télécharge environ 12 Mo.',
  'dropzone.dropHere': 'Déposez les reçus ici',
  'dropzone.dropToUpload': 'Déposer pour téléverser',
  'dropzone.subtitle': 'un ou plusieurs — ou collez depuis le presse-papiers, ou',
  'dropzone.chooseFile': 'Choisir un fichier',
  'dropzone.chooseFileAria': 'Choisir des fichiers de reçus',
  'dropzone.takePhoto': 'Prendre une photo',
  'dropzone.takePhotoAria': 'Capturer un reçu avec l’appareil photo',
  'dropzone.accepted': 'Acceptés : {extensions}',
  'dropzone.unsupported.one':
    'Type de fichier non pris en charge. Acceptés : {accepted}',
  'dropzone.unsupported.many':
    'Types de fichiers non pris en charge. Acceptés : {accepted}',
  'dropzone.skipped.one': 'Ignoré : 1 fichier non pris en charge.',
  'dropzone.skipped.many': 'Ignorés : {n} fichiers non pris en charge.',
  'dropzone.regionLabel': 'Zone de dépôt de reçus',
  'scan.another': 'Scanner un autre',
  'sessions.empty': 'Déposez un ou plusieurs reçus pour commencer.',
  'sessions.search.placeholder': 'Rechercher marchand, article ou fichier',
  'sessions.search.empty': 'Aucun reçu ne correspond à la recherche.',
  'sessions.count.partial.one': '1 sur {total}',
  'sessions.count.partial.many': '{n} sur {total}',
  'sessions.trySample': 'Essayer avec un reçu d’exemple',
  'sessions.count.one': '1 reçu',
  'sessions.count.many': '{n} reçus',
  'sessions.clearDone': 'Effacer les terminés ({n})',
  'sessions.clearDoneTooltip':
    'Retirer de la liste tous les reçus terminés (réussite ou erreur)',
  'sessions.clearAll': 'Tout effacer',
  'sessions.clearAllTooltip.inFlight':
    'Annuler le travail en cours et retirer tous les reçus',
  'sessions.clearAllTooltip.idle': 'Retirer tous les reçus de la liste',
  'sessions.removeFromList': 'Retirer de la liste',
  'sessions.removeAriaLabel': 'Retirer {filename} de la liste',
  'sessions.lowQuality.title':
    'La qualité de la photo est trop faible pour lire ce reçu de façon fiable.',
  'sessions.lowQuality.body':
    'Pour de meilleurs résultats, reprenez la photo avec le reçu à plat, bien éclairé, entièrement dans le cadre, sans reflet ni ombre de la main sur le papier. Les champs ci-dessous restent affichés mais peuvent être incomplets ou erronés — modifiez-les manuellement si nécessaire.',
  'sessions.recropManually': 'Recadrer manuellement',
  'sessions.recropTooltip':
    'Ajuster manuellement les coins du reçu et retraiter',
  'sessions.rotate': 'Pivoter 90°',
  'sessions.rotateTooltip':
    'Faire pivoter l’image du reçu de 90° dans le sens horaire et retraiter',
  'sessions.duplicate': 'Doublon possible',
  'sessions.duplicateTooltip':
    'Un autre reçu de ce lot a le même marchand, la même date et le même total',
  'sessions.dragHandle': 'Glisser pour réordonner',
  'sessions.dragHandleAria': 'Glisser pour réordonner, {filename}',
  'sessions.previewTooltip': "Voir l'image scannée",
  'sessions.previewAria': "Voir l'image scannée, {filename}",
  'sessions.cropPreview.hint':
    "Aperçu du recadrage automatique — s'il semble incorrect, recadrez-le manuellement maintenant ou supprimez le reçu et reprenez la photo.",
  'state.queued': 'En file',
  'state.done': 'Terminé',
  'state.review': 'Vérifier le recadrage',
  'sessions.confirmScan': 'C’est bon — numériser',
  'state.error': 'Erreur',
  'state.lowQuality': 'Qualité faible',
  'stage.load': 'Chargement',
  'stage.preprocess': 'Prétraitement',
  'stage.ocr': 'OCR en cours',
  'stage.parse': 'Extraction des champs',
  'stage.validate': 'Validation',
  'stage.done': 'Terminé',
  'export.empty': 'Aucun reçu prêt',
  'export.one': 'Exporter 1 reçu',
  'export.many': 'Tout exporter ({n} reçus)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Télécharger tous les reçus en ZIP : chaque image en marchand_Mois_AA.jpg (regroupées par marchand) plus un classeur avec une feuille de synthèse et un détail par reçu',
  'export.billCsv': 'CSV factures',
  'export.billCsvTooltip':
    'Télécharger tous les reçus au format d’import de factures : une ligne par article, dates JJ/MM/AAAA, colonnes obligatoires * conservées',
  'taxRate.label': 'Taux de TVA',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'Taux de TVA de ce reçu. Laissez vide pour le déduire de la taxe détectée. Utilisé comme *LineTaxCode dans le CSV factures.',
  'taxRate.applyAll': 'Appliquer à tous',
  'taxRate.applyAllTooltip': 'Copier ce taux de TVA sur tous les autres reçus du lot',
  'export.regionLabel': 'Options d’exportation',
  'batch.processing': 'Traitement — {done} sur {total} terminés',
  'batch.allDone.one': '1 reçu terminé',
  'batch.allDone.many': 'Les {total} reçus sont terminés',
  'batch.allDoneWithErrors.one': '1 reçu terminé ({errors} erreur{s})',
  'batch.allDoneWithErrors.many':
    'Les {total} reçus sont terminés ({errors} erreur{s})',
  'batch.grandTotal': 'Total général',
  'batch.partial': '{done} sur {total} terminés',
  'receipt.heading': 'Reçu',
  'receipt.lineItems': 'Articles',
  'receipt.empty': 'Déposez un reçu pour commencer.',
  'receipt.noItems': 'Aucun article détecté.',
  'receipt.fields.vendor': 'Marchand',
  'receipt.fields.date': 'Date',
  'receipt.fields.currency': 'Devise',
  'receipt.fields.subtotal': 'Sous-total',
  'receipt.fields.tax': 'Taxe',
  'receipt.fields.tip': 'Pourboire',
  'receipt.fields.total': 'Total',
  'receipt.fields.payment': 'Paiement',
  'receipt.cols.item': 'Article',
  'receipt.cols.qty': 'Qté',
  'receipt.cols.unitPrice': 'Prix unit.',
  'receipt.cols.total': 'Total',
  'receipt.addItem': 'Ajouter un article',
  'receipt.removeItem': 'Retirer l’article, ligne {n}',
  'receipt.dragHandle': 'Glisser pour réordonner',
  'receipt.dragHandleAria': 'Glisser pour réordonner, ligne {n}',
  'crop.title': 'Recadrage manuel',
  'crop.description':
    'Touchez les quatre coins du reçu : d’abord le coin inférieur gauche, puis dans le sens horaire. Faites glisser un point pour l’ajuster, puis Appliquer.',
  'crop.cancel': 'Annuler',
  'crop.apply': 'Appliquer le recadrage',
  'crop.close': 'Fermer',
  'crop.cornerAria': 'Coin {n}',
  'crop.tapBottomLeft': 'Touchez le coin inférieur gauche du reçu',
  'crop.tapTopLeft': 'Maintenant le coin supérieur gauche',
  'crop.tapTopRight': 'Maintenant le coin supérieur droit',
  'crop.tapBottomRight': 'Enfin le coin inférieur droit',
  'crop.reset': 'Recommencer',
  'confidence.high': 'élevée',
  'confidence.medium': 'moyenne',
  'confidence.low': 'faible',
  'theme.toLight': 'Passer au thème clair',
  'theme.toDark': 'Passer au thème sombre',
  'debug.show': 'Afficher les infos de débogage',
  'persistence.label': 'Conserver le lot sur cet appareil',
  'persistence.tooltip':
    'Conserve les reçus traités dans ce navigateur afin qu’ils survivent à un rechargement. Stockage local uniquement — rien ne quitte l’appareil.',
  'excludeTax.label': 'Exclure la TVA des exports',
  'excludeTax.tooltip':
    'Quand activé, les infos de TVA sont omises des exports CSV / Excel / ZIP. Utile dans les juridictions où exporter les données fiscales est risqué. Le sous-total est aussi masqué car il permettrait de calculer la TVA (total − sous-total). Le pourboire est indépendant et reste inclus.',
  'consent.message':
    "Vos reçus ne quittent jamais votre appareil. Nous utilisons des cookies pour l'analytique et la publicité — acceptez pour soutenir le site, ou refusez pour les ignorer.",
  'consent.accept': 'Accepter',
  'consent.reject': 'Refuser',
  'consent.privacy': 'Politique de confidentialité',
  'consent.preferences': 'Préférences de cookies',
  'donate.title': 'Soutenir Receipt Ripper',
  'donate.body': 'Gratuit, financé par la publicité, et vos reçus ne quittent jamais votre appareil. Les dons aident à maintenir cela.',
  'donate.cta': 'Faire un don via Stripe',
  'donate.skip': 'Passer',
  'donate.countdown': 'Votre téléchargement démarre dans {seconds} s',
  'donate.starting': 'Démarrage du téléchargement…',
  'validation.totals.title':
    'Articles + taxe + pourboire ne correspondent pas au total',
  'validation.totals.body':
    'Articles + taxe + pourboire = {expected}, mais le total indique {actual} (écart {gap}). Vérifiez les totaux des articles.',
  'summary.title': 'Résumé du lot',
  'summary.byCategory': 'Par catégorie',
  'summary.topVendors': 'Principaux marchands',
  'summary.byMonth': 'Par mois',
  'tags.label': 'Étiquettes',
  'tags.placeholder': 'Ajouter une étiquette…',
  'tags.removeAria': 'Retirer l’étiquette {tag}',
  'notes.label': 'Notes',
  'notes.placeholder': 'Notes sur ce reçu…',
  'category.label': 'Catégorie',
  'category.placeholder': 'Sans catégorie',
  'category.autoBadge': 'auto · {pct} %',
  'category.autoTooltip':
    'Détectée automatiquement à partir du marchand + des articles ({pct} % de confiance). Choisissez une catégorie pour confirmer ou modifier.',
  'category.food': 'Restauration',
  'category.transport': 'Transport',
  'category.office': 'Bureau',
  'category.healthcare': 'Santé',
  'category.travel': 'Voyages',
  'category.utilities': 'Services',
  'category.entertainment': 'Loisirs',
  'category.subscriptions': 'Abonnements',
  'category.personal': 'Personnel',
  'category.other': 'Autre',
};

const deu: Strings = {
  'tagline.onDevice': 'Ihre Belege verlassen Ihr Gerät nicht',
  'footer.dataPrivacy': 'Der Inhalt deiner Belege verlässt niemals dieses Gerät.',
  'footer.licenses': 'Open-Source-Lizenzen',
  'footer.about': 'Über uns',
  'footer.faq': 'FAQ',
  'footer.guides': 'Leitfäden',
  'footer.contact': 'Kontakt',
  'footer.privacy': 'Datenschutz',
  'footer.terms': 'Nutzungsbedingungen',
  'nav.home': 'Startseite',
  'language.label': 'Oberfläche',
  'language.receiptLabel': 'Belege',
  'receiptLang.title': 'In welcher Sprache sind Ihre Belege?',
  'receiptLang.body': 'Damit wird das OCR-Modell gewählt, das Ihren Beleg liest — nicht die Sprache der Oberfläche.',
  'receiptLang.confirmSame': 'Ja, {language}',
  'receiptLang.chooseOther': 'Nein, eine andere Sprache',
  'receiptLang.start': 'Scan starten',
  'receiptLang.cardLabel': 'Belegsprache',
  'language.firstUseDownload':
    'Die erste Nutzung einer neuen Sprache lädt ca. 12 MB herunter.',
  'dropzone.dropHere': 'Belege hier ablegen',
  'dropzone.dropToUpload': 'Zum Hochladen ablegen',
  'dropzone.subtitle': 'einer oder mehrere — oder aus der Zwischenablage einfügen, oder',
  'dropzone.chooseFile': 'Datei wählen',
  'dropzone.chooseFileAria': 'Belegdateien auswählen',
  'dropzone.takePhoto': 'Foto aufnehmen',
  'dropzone.takePhotoAria': 'Beleg mit der Kamera aufnehmen',
  'dropzone.accepted': 'Akzeptiert: {extensions}',
  'dropzone.unsupported.one': 'Dateityp nicht unterstützt. Akzeptiert: {accepted}',
  'dropzone.unsupported.many':
    'Dateitypen nicht unterstützt. Akzeptiert: {accepted}',
  'dropzone.skipped.one': 'Übersprungen: 1 nicht unterstützte Datei.',
  'dropzone.skipped.many': 'Übersprungen: {n} nicht unterstützte Dateien.',
  'dropzone.regionLabel': 'Ablagebereich für Belegdateien',
  'scan.another': 'Weiteren scannen',
  'sessions.empty': 'Lege einen oder mehrere Belege ab, um zu beginnen.',
  'sessions.search.placeholder': 'Händler, Position oder Datei suchen',
  'sessions.search.empty': 'Keine Belege passen zur Suche.',
  'sessions.count.partial.one': '1 von {total}',
  'sessions.count.partial.many': '{n} von {total}',
  'sessions.trySample': 'Mit einem Beispielbeleg ausprobieren',
  'sessions.count.one': '1 Beleg',
  'sessions.count.many': '{n} Belege',
  'sessions.clearDone': 'Erledigte löschen ({n})',
  'sessions.clearDoneTooltip':
    'Alle abgeschlossenen Belege (Erfolg oder Fehler) aus der Liste entfernen',
  'sessions.clearAll': 'Alles löschen',
  'sessions.clearAllTooltip.inFlight':
    'Laufende Verarbeitung abbrechen und alle Belege entfernen',
  'sessions.clearAllTooltip.idle': 'Alle Belege aus der Liste entfernen',
  'sessions.removeFromList': 'Aus der Liste entfernen',
  'sessions.removeAriaLabel': '{filename} aus der Liste entfernen',
  'sessions.lowQuality.title':
    'Die Fotoqualität ist zu gering, um diesen Beleg zuverlässig zu lesen.',
  'sessions.lowQuality.body':
    'Mache das Foto erneut mit dem Beleg flach, gut beleuchtet, vollständig im Bild und ohne Spiegelungen oder Handschatten auf dem Papier. Die Felder unten werden weiterhin angezeigt, können aber unvollständig oder falsch sein — bei Bedarf manuell bearbeiten.',
  'sessions.recropManually': 'Manuell zuschneiden',
  'sessions.recropTooltip':
    'Belegecken manuell anpassen und neu verarbeiten',
  'sessions.rotate': 'Um 90° drehen',
  'sessions.rotateTooltip':
    'Das Belegbild um 90° im Uhrzeigersinn drehen und neu verarbeiten',
  'sessions.duplicate': 'Mögliches Duplikat',
  'sessions.duplicateTooltip':
    'Ein anderer Beleg in diesem Stapel hat denselben Händler, dasselbe Datum und denselben Betrag',
  'sessions.dragHandle': 'Zum Umsortieren ziehen',
  'sessions.dragHandleAria': 'Zum Umsortieren ziehen, {filename}',
  'sessions.previewTooltip': 'Gescanntes Bild anzeigen',
  'sessions.previewAria': 'Gescanntes Bild anzeigen, {filename}',
  'sessions.cropPreview.hint':
    'Vorschau des automatischen Zuschnitts – wenn er falsch aussieht, jetzt manuell zuschneiden oder den Beleg entfernen und das Foto erneut aufnehmen.',
  'state.queued': 'In Warteschlange',
  'state.done': 'Fertig',
  'state.review': 'Zuschnitt prüfen',
  'sessions.confirmScan': 'Sieht gut aus – scannen',
  'state.error': 'Fehler',
  'state.lowQuality': 'Geringe Qualität',
  'stage.load': 'Lädt',
  'stage.preprocess': 'Vorverarbeitung',
  'stage.ocr': 'OCR läuft',
  'stage.parse': 'Felder werden extrahiert',
  'stage.validate': 'Validierung',
  'stage.done': 'Fertig',
  'export.empty': 'Keine Belege bereit',
  'export.one': '1 Beleg exportieren',
  'export.many': 'Alle exportieren ({n} Belege)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Alle Belege als ZIP herunterladen: jedes Bild als haendler_Monat_JJ.jpg (nach Händler gruppiert) plus eine Arbeitsmappe mit Übersichtsblatt und Einzeldetails',
  'export.billCsv': 'Rechnungs-CSV',
  'export.billCsvTooltip':
    'Alle Belege im Rechnungsimport-Format herunterladen: eine Zeile pro Position, Datum TT/MM/JJJJ, Pflichtspalten * unverändert',
  'taxRate.label': 'MwSt.-Satz',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'MwSt.-Satz dieses Belegs. Leer lassen, um ihn aus der erkannten Steuer abzuleiten. Wird als *LineTaxCode in der Rechnungs-CSV verwendet.',
  'taxRate.applyAll': 'Auf alle anwenden',
  'taxRate.applyAllTooltip': 'Diesen MwSt.-Satz auf alle anderen Belege im Stapel übertragen',
  'export.regionLabel': 'Exportoptionen',
  'batch.processing': 'Verarbeitung — {done} von {total} fertig',
  'batch.allDone.one': '1 Beleg fertig',
  'batch.allDone.many': 'Alle {total} Belege sind fertig',
  'batch.allDoneWithErrors.one': '1 Beleg fertig ({errors} Fehler)',
  'batch.allDoneWithErrors.many':
    'Alle {total} Belege sind fertig ({errors} Fehler)',
  'batch.grandTotal': 'Gesamtsumme',
  'batch.partial': '{done} von {total} fertig',
  'receipt.heading': 'Beleg',
  'receipt.lineItems': 'Positionen',
  'receipt.empty': 'Lege einen Beleg ab, um zu beginnen.',
  'receipt.noItems': 'Keine Positionen erkannt.',
  'receipt.fields.vendor': 'Händler',
  'receipt.fields.date': 'Datum',
  'receipt.fields.currency': 'Währung',
  'receipt.fields.subtotal': 'Zwischensumme',
  'receipt.fields.tax': 'MwSt.',
  'receipt.fields.tip': 'Trinkgeld',
  'receipt.fields.total': 'Gesamt',
  'receipt.fields.payment': 'Zahlung',
  'receipt.cols.item': 'Artikel',
  'receipt.cols.qty': 'Menge',
  'receipt.cols.unitPrice': 'Einzelpreis',
  'receipt.cols.total': 'Gesamt',
  'receipt.addItem': 'Position hinzufügen',
  'receipt.removeItem': 'Artikel entfernen, Zeile {n}',
  'receipt.dragHandle': 'Zum Umsortieren ziehen',
  'receipt.dragHandleAria': 'Zum Umsortieren ziehen, Zeile {n}',
  'crop.title': 'Manueller Zuschnitt',
  'crop.description':
    'Tippe auf die vier Ecken des Belegs — zuerst unten links, dann im Uhrzeigersinn. Ziehe einen Punkt zum Feinjustieren und wähle dann Anwenden.',
  'crop.cancel': 'Abbrechen',
  'crop.apply': 'Zuschnitt anwenden',
  'crop.close': 'Schließen',
  'crop.cornerAria': 'Ecke {n}',
  'crop.tapBottomLeft': 'Tippe auf die untere linke Ecke des Belegs',
  'crop.tapTopLeft': 'Jetzt die obere linke Ecke',
  'crop.tapTopRight': 'Jetzt die obere rechte Ecke',
  'crop.tapBottomRight': 'Zuletzt die untere rechte Ecke',
  'crop.reset': 'Neu beginnen',
  'confidence.high': 'hoch',
  'confidence.medium': 'mittel',
  'confidence.low': 'niedrig',
  'theme.toLight': 'Zu hellem Theme wechseln',
  'theme.toDark': 'Zu dunklem Theme wechseln',
  'debug.show': 'Debug-Info anzeigen',
  'persistence.label': 'Stapel auf diesem Gerät behalten',
  'persistence.tooltip':
    'Verarbeitete Belege im Browser speichern, damit sie ein Neuladen überstehen. Nur lokal gespeichert — nichts verlässt das Gerät.',
  'excludeTax.label': 'MwSt aus Exporten ausschließen',
  'excludeTax.tooltip':
    'Wenn aktiviert, wird die MwSt aus CSV-/Excel-/ZIP-Exporten weggelassen. Nützlich in Rechtsräumen, in denen das Exportieren von Steuerdaten riskant ist. Die Zwischensumme wird ebenfalls ausgeblendet, da sie das Berechnen der MwSt erlauben würde (Gesamt − Zwischensumme). Trinkgeld ist davon unabhängig und bleibt enthalten.',
  'consent.message':
    'Ihre Belege verlassen Ihr Gerät nicht. Wir nutzen Cookies für Analytik und Werbung — akzeptieren Sie, um die Seite zu unterstützen, oder lehnen Sie sie ab.',
  'consent.accept': 'Akzeptieren',
  'consent.reject': 'Ablehnen',
  'consent.privacy': 'Datenschutzerklärung',
  'consent.preferences': 'Cookie-Einstellungen',
  'donate.title': 'Receipt Ripper unterstützen',
  'donate.body': 'Kostenlos, werbefinanziert, und Ihre Belege verlassen nie Ihr Gerät. Spenden helfen, das so zu halten.',
  'donate.cta': 'Mit Stripe spenden',
  'donate.skip': 'Überspringen',
  'donate.countdown': 'Ihr Download startet in {seconds} Sek.',
  'donate.starting': 'Download wird gestartet…',
  'validation.totals.title':
    'Positionen + MwSt. + Trinkgeld stimmen nicht mit dem Gesamtbetrag überein',
  'validation.totals.body':
    'Positionen + MwSt. + Trinkgeld = {expected}, aber das Gesamt zeigt {actual} (Abweichung {gap}). Prüfe die Positionsbeträge.',
  'summary.title': 'Stapelübersicht',
  'summary.byCategory': 'Nach Kategorie',
  'summary.topVendors': 'Top-Händler',
  'summary.byMonth': 'Nach Monat',
  'tags.label': 'Tags',
  'tags.placeholder': 'Tag hinzufügen…',
  'tags.removeAria': 'Tag {tag} entfernen',
  'notes.label': 'Notizen',
  'notes.placeholder': 'Notizen zu diesem Beleg…',
  'category.label': 'Kategorie',
  'category.placeholder': 'Nicht kategorisiert',
  'category.autoBadge': 'auto · {pct} %',
  'category.autoTooltip':
    'Automatisch aus Händler + Positionen erkannt ({pct} % sicher). Wähle eine Kategorie, um zu bestätigen oder zu ändern.',
  'category.food': 'Essen',
  'category.transport': 'Transport',
  'category.office': 'Büro',
  'category.healthcare': 'Gesundheit',
  'category.travel': 'Reisen',
  'category.utilities': 'Nebenkosten',
  'category.entertainment': 'Unterhaltung',
  'category.subscriptions': 'Abonnements',
  'category.personal': 'Persönlich',
  'category.other': 'Sonstiges',
};

const nld: Strings = {
  'tagline.onDevice': 'Uw bonnen verlaten dit apparaat nooit',
  'footer.dataPrivacy': 'De inhoud van je bonnen verlaat dit apparaat nooit.',
  'footer.licenses': 'Open-source-licenties',
  'footer.about': 'Over ons',
  'footer.faq': 'FAQ',
  'footer.guides': 'Gidsen',
  'footer.contact': 'Contact',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Voorwaarden',
  'nav.home': 'Home',
  'language.label': 'Interface',
  'language.receiptLabel': 'Bonnen',
  'receiptLang.title': 'In welke taal zijn uw bonnen?',
  'receiptLang.body': 'Dit bepaalt het OCR-model dat uw bon leest, niet de taal van de interface.',
  'receiptLang.confirmSame': 'Ja, {language}',
  'receiptLang.chooseOther': 'Nee, een andere taal',
  'receiptLang.start': 'Scannen starten',
  'receiptLang.cardLabel': 'Taal van de bon',
  'language.firstUseDownload':
    'Eerste gebruik van een nieuwe taal downloadt ~12 MB.',
  'dropzone.dropHere': 'Sleep bonnen hierheen',
  'dropzone.dropToUpload': 'Loslaten om te uploaden',
  'dropzone.subtitle': 'een of meer — of plak vanaf het klembord, of',
  'dropzone.chooseFile': 'Bestand kiezen',
  'dropzone.chooseFileAria': 'Bonbestanden kiezen',
  'dropzone.takePhoto': 'Foto maken',
  'dropzone.takePhotoAria': 'Een bon vastleggen met de camera',
  'dropzone.accepted': 'Toegestaan: {extensions}',
  'dropzone.unsupported.one':
    'Bestandstype niet ondersteund. Toegestaan: {accepted}',
  'dropzone.unsupported.many':
    'Bestandstypen niet ondersteund. Toegestaan: {accepted}',
  'dropzone.skipped.one': 'Overgeslagen: 1 niet-ondersteund bestand.',
  'dropzone.skipped.many': 'Overgeslagen: {n} niet-ondersteunde bestanden.',
  'dropzone.regionLabel': 'Bonbestand-sleepzone',
  'scan.another': 'Nog een scannen',
  'sessions.empty': 'Sleep een of meer bonnen hierheen om te beginnen.',
  'sessions.search.placeholder': 'Zoek verkoper, regel of bestand',
  'sessions.search.empty': 'Geen bonnen komen overeen met de zoekopdracht.',
  'sessions.count.partial.one': '1 van {total}',
  'sessions.count.partial.many': '{n} van {total}',
  'sessions.trySample': 'Probeer met een voorbeeldbon',
  'sessions.count.one': '1 bon',
  'sessions.count.many': '{n} bonnen',
  'sessions.clearDone': 'Klaar verwijderen ({n})',
  'sessions.clearDoneTooltip':
    'Alle voltooide bonnen (succes of fout) uit de lijst verwijderen',
  'sessions.clearAll': 'Alles wissen',
  'sessions.clearAllTooltip.inFlight':
    'Lopend werk annuleren en alle bonnen verwijderen',
  'sessions.clearAllTooltip.idle': 'Alle bonnen uit de lijst verwijderen',
  'sessions.removeFromList': 'Uit de lijst verwijderen',
  'sessions.removeAriaLabel': '{filename} uit de lijst verwijderen',
  'sessions.lowQuality.title':
    'De fotokwaliteit is te laag om deze bon betrouwbaar te lezen.',
  'sessions.lowQuality.body':
    'Maak voor betere resultaten een nieuwe foto met de bon plat, goed verlicht, volledig in beeld en zonder schittering of schaduw van je hand op het papier. De velden hieronder blijven zichtbaar maar kunnen onvolledig of onjuist zijn — pas ze indien nodig handmatig aan.',
  'sessions.recropManually': 'Handmatig bijsnijden',
  'sessions.recropTooltip':
    'Bonhoeken handmatig aanpassen en opnieuw verwerken',
  'sessions.rotate': '90° draaien',
  'sessions.rotateTooltip':
    'De bonafbeelding 90° met de klok mee draaien en opnieuw verwerken',
  'sessions.duplicate': 'Mogelijk duplicaat',
  'sessions.duplicateTooltip':
    'Een andere bon in deze partij heeft dezelfde verkoper, datum en totaal',
  'sessions.dragHandle': 'Slepen om te herschikken',
  'sessions.dragHandleAria': 'Slepen om te herschikken, {filename}',
  'sessions.previewTooltip': 'Gescande afbeelding bekijken',
  'sessions.previewAria': 'Gescande afbeelding bekijken, {filename}',
  'sessions.cropPreview.hint':
    'Voorbeeld van automatisch bijsnijden — ziet het er verkeerd uit, snijd dan nu handmatig bij of verwijder de bon en maak de foto opnieuw.',
  'state.queued': 'In wacht',
  'state.done': 'Klaar',
  'state.review': 'Bijsnijden controleren',
  'sessions.confirmScan': 'Ziet er goed uit — scannen',
  'state.error': 'Fout',
  'state.lowQuality': 'Lage kwaliteit',
  'stage.load': 'Laden',
  'stage.preprocess': 'Voorbewerking',
  'stage.ocr': 'OCR uitvoeren',
  'stage.parse': 'Velden extraheren',
  'stage.validate': 'Valideren',
  'stage.done': 'Klaar',
  'export.empty': 'Geen bonnen klaar',
  'export.one': '1 bon exporteren',
  'export.many': 'Alle exporteren ({n} bonnen)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Alle bonnen downloaden als ZIP: elke afbeelding als winkel_Maand_JJ.jpg (gegroepeerd per winkel) plus een werkmap met overzichtsblad en details per bon',
  'export.billCsv': 'Facturen-CSV',
  'export.billCsvTooltip':
    'Alle bonnen downloaden in de factuurimport-indeling: één regel per post, datums DD/MM/JJJJ, verplichte *-kolommen ongewijzigd',
  'taxRate.label': 'Btw-tarief',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'Btw-percentage van deze bon. Laat leeg om het af te leiden uit de herkende belasting. Wordt gebruikt als *LineTaxCode in de facturen-CSV.',
  'taxRate.applyAll': 'Op alle toepassen',
  'taxRate.applyAllTooltip': 'Dit btw-tarief kopiëren naar alle andere bonnen in de batch',
  'export.regionLabel': 'Exportopties',
  'batch.processing': 'Bezig — {done} van {total} klaar',
  'batch.allDone.one': '1 bon klaar',
  'batch.allDone.many': 'Alle {total} bonnen klaar',
  'batch.allDoneWithErrors.one': '1 bon klaar ({errors} fout{s})',
  'batch.allDoneWithErrors.many':
    'Alle {total} bonnen klaar ({errors} fout{s})',
  'batch.grandTotal': 'Eindtotaal',
  'batch.partial': '{done} van {total} klaar',
  'receipt.heading': 'Bon',
  'receipt.lineItems': 'Regels',
  'receipt.empty': 'Sleep een bon hierheen om te beginnen.',
  'receipt.noItems': 'Geen regels gedetecteerd.',
  'receipt.fields.vendor': 'Verkoper',
  'receipt.fields.date': 'Datum',
  'receipt.fields.currency': 'Valuta',
  'receipt.fields.subtotal': 'Subtotaal',
  'receipt.fields.tax': 'BTW',
  'receipt.fields.tip': 'Fooi',
  'receipt.fields.total': 'Totaal',
  'receipt.fields.payment': 'Betaling',
  'receipt.cols.item': 'Artikel',
  'receipt.cols.qty': 'Aant.',
  'receipt.cols.unitPrice': 'Stuksprijs',
  'receipt.cols.total': 'Totaal',
  'receipt.addItem': 'Regel toevoegen',
  'receipt.removeItem': 'Artikel verwijderen, rij {n}',
  'receipt.dragHandle': 'Slepen om te herschikken',
  'receipt.dragHandleAria': 'Slepen om te herschikken, rij {n}',
  'crop.title': 'Handmatig bijsnijden',
  'crop.description':
    'Tik op de vier hoeken van de bon — eerst linksonder, daarna met de klok mee. Sleep een punt om bij te stellen en druk dan op Toepassen.',
  'crop.cancel': 'Annuleren',
  'crop.apply': 'Bijsnijden toepassen',
  'crop.close': 'Sluiten',
  'crop.cornerAria': 'Hoek {n}',
  'crop.tapBottomLeft': 'Tik op de hoek linksonder van de bon',
  'crop.tapTopLeft': 'Nu de hoek linksboven',
  'crop.tapTopRight': 'Nu de hoek rechtsboven',
  'crop.tapBottomRight': 'Tot slot de hoek rechtsonder',
  'crop.reset': 'Opnieuw beginnen',
  'confidence.high': 'hoog',
  'confidence.medium': 'gemiddeld',
  'confidence.low': 'laag',
  'theme.toLight': 'Naar licht thema schakelen',
  'theme.toDark': 'Naar donker thema schakelen',
  'debug.show': 'Debuginfo tonen',
  'persistence.label': 'Partij op dit apparaat bewaren',
  'persistence.tooltip':
    'Bewaar verwerkte bonnen in deze browser zodat ze een herlaad overleven. Alleen lokaal opgeslagen — niets verlaat het apparaat.',
  'excludeTax.label': 'BTW uitsluiten van exports',
  'excludeTax.tooltip':
    'Indien aan, wordt BTW-informatie weggelaten uit CSV-/Excel-/ZIP-exports. Handig in rechtsgebieden waar het exporteren van belastinggegevens risicovol is. Het subtotaal wordt ook verborgen omdat het de BTW zou onthullen (totaal − subtotaal). Fooi staat los van de BTW en blijft behouden.',
  'consent.message':
    'Je bonnen verlaten je apparaat nooit. We gebruiken cookies voor analytics en advertenties — accepteer om de site te steunen, of weiger om ze over te slaan.',
  'consent.accept': 'Accepteren',
  'consent.reject': 'Weigeren',
  'consent.privacy': 'Privacybeleid',
  'consent.preferences': 'Cookie-voorkeuren',
  'donate.title': 'Steun Receipt Ripper',
  'donate.body': 'Gratis, gefinancierd door advertenties, en uw bonnen verlaten nooit uw apparaat. Donaties helpen het zo te houden.',
  'donate.cta': 'Doneren via Stripe',
  'donate.skip': 'Overslaan',
  'donate.countdown': 'Uw download begint over {seconds}s',
  'donate.starting': 'Download wordt gestart…',
  'validation.totals.title':
    'Regels + BTW + fooi komen niet overeen met het totaal',
  'validation.totals.body':
    'Regels + BTW + fooi = {expected}, maar het totaal staat op {actual} (verschil {gap}). Controleer de regeltotalen.',
  'summary.title': 'Partijoverzicht',
  'summary.byCategory': 'Per categorie',
  'summary.topVendors': 'Top verkopers',
  'summary.byMonth': 'Per maand',
  'tags.label': 'Labels',
  'tags.placeholder': 'Label toevoegen…',
  'tags.removeAria': 'Label {tag} verwijderen',
  'notes.label': 'Notities',
  'notes.placeholder': 'Notities over deze bon…',
  'category.label': 'Categorie',
  'category.placeholder': 'Geen categorie',
  'category.autoBadge': 'auto · {pct}%',
  'category.autoTooltip':
    'Automatisch herkend op basis van verkoper + regels ({pct}% zeker). Kies een categorie om te bevestigen of te wijzigen.',
  'category.food': 'Eten',
  'category.transport': 'Vervoer',
  'category.office': 'Kantoor',
  'category.healthcare': 'Zorg',
  'category.travel': 'Reizen',
  'category.utilities': 'Nutsvoorzieningen',
  'category.entertainment': 'Vermaak',
  'category.subscriptions': 'Abonnementen',
  'category.personal': 'Persoonlijk',
  'category.other': 'Overig',
};

const ita: Strings = {
  'tagline.onDevice': 'Le tue ricevute non lasciano mai il tuo dispositivo',
  'footer.dataPrivacy': 'Il contenuto delle tue ricevute non lascia mai questo dispositivo.',
  'footer.licenses': 'Licenze open source',
  'footer.about': 'Chi siamo',
  'footer.faq': 'FAQ',
  'footer.guides': 'Guide',
  'footer.contact': 'Contatti',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Termini',
  'nav.home': 'Home',
  'language.label': 'Interfaccia',
  'language.receiptLabel': 'Scontrini',
  'receiptLang.title': 'In che lingua sono i tuoi scontrini?',
  'receiptLang.body': 'Questo seleziona il modello OCR che legge il tuo scontrino, non la lingua dell’interfaccia.',
  'receiptLang.confirmSame': 'Sì, {language}',
  'receiptLang.chooseOther': 'No, un’altra lingua',
  'receiptLang.start': 'Inizia la scansione',
  'receiptLang.cardLabel': 'Lingua dello scontrino',
  'language.firstUseDownload':
    'Il primo utilizzo di una nuova lingua scarica circa 12 MB.',
  'dropzone.dropHere': 'Trascina le ricevute qui',
  'dropzone.dropToUpload': 'Rilascia per caricare',
  'dropzone.subtitle': 'una o più — o incolla dagli appunti, oppure',
  'dropzone.chooseFile': 'Scegli un file',
  'dropzone.chooseFileAria': 'Scegli i file delle ricevute',
  'dropzone.takePhoto': 'Scatta una foto',
  'dropzone.takePhotoAria': 'Acquisisci una ricevuta con la fotocamera',
  'dropzone.accepted': 'Accettati: {extensions}',
  'dropzone.unsupported.one': 'Tipo di file non supportato. Accettati: {accepted}',
  'dropzone.unsupported.many':
    'Tipi di file non supportati. Accettati: {accepted}',
  'dropzone.skipped.one': 'Saltato: 1 file non supportato.',
  'dropzone.skipped.many': 'Saltati: {n} file non supportati.',
  'dropzone.regionLabel': 'Area di rilascio per i file delle ricevute',
  'scan.another': 'Scansiona un altro',
  'sessions.empty': 'Trascina una o più ricevute per iniziare.',
  'sessions.search.placeholder': 'Cerca esercente, voce o file',
  'sessions.search.empty': 'Nessuna ricevuta corrisponde alla ricerca.',
  'sessions.count.partial.one': '1 di {total}',
  'sessions.count.partial.many': '{n} di {total}',
  'sessions.trySample': 'Prova con una ricevuta di esempio',
  'sessions.count.one': '1 ricevuta',
  'sessions.count.many': '{n} ricevute',
  'sessions.clearDone': 'Cancella completate ({n})',
  'sessions.clearDoneTooltip':
    'Rimuovere dall’elenco tutte le ricevute completate (riuscite o con errore)',
  'sessions.clearAll': 'Cancella tutto',
  'sessions.clearAllTooltip.inFlight':
    'Annullare il lavoro in corso e rimuovere tutte le ricevute',
  'sessions.clearAllTooltip.idle': 'Rimuovere tutte le ricevute dall’elenco',
  'sessions.removeFromList': 'Rimuovi dall’elenco',
  'sessions.removeAriaLabel': 'Rimuovi {filename} dall’elenco',
  'sessions.lowQuality.title':
    'La qualità della foto è troppo bassa per leggere questa ricevuta in modo affidabile.',
  'sessions.lowQuality.body':
    'Per risultati migliori, riscatta la foto con la ricevuta piatta, ben illuminata, completamente nell’inquadratura e senza riflessi o ombre della mano sulla carta. I campi sotto restano visibili ma possono essere incompleti o errati — modificali manualmente se necessario.',
  'sessions.recropManually': 'Ritaglia manualmente',
  'sessions.recropTooltip':
    'Regola manualmente gli angoli della ricevuta e rielabora',
  'sessions.rotate': 'Ruota di 90°',
  'sessions.rotateTooltip':
    'Ruota l’immagine della ricevuta di 90° in senso orario e rielabora',
  'sessions.duplicate': 'Possibile duplicato',
  'sessions.duplicateTooltip':
    'Un’altra ricevuta in questo lotto ha lo stesso esercente, data e totale',
  'sessions.dragHandle': 'Trascina per riordinare',
  'sessions.dragHandleAria': 'Trascina per riordinare, {filename}',
  'sessions.previewTooltip': 'Visualizza immagine scansionata',
  'sessions.previewAria': 'Visualizza immagine scansionata, {filename}',
  'sessions.cropPreview.hint':
    'Anteprima del ritaglio automatico: se non sembra corretto, ritaglia manualmente ora oppure rimuovi lo scontrino e rifai la foto.',
  'state.queued': 'In coda',
  'state.done': 'Fatto',
  'state.review': 'Controlla ritaglio',
  'sessions.confirmScan': 'Va bene — scansiona',
  'state.error': 'Errore',
  'state.lowQuality': 'Bassa qualità',
  'stage.load': 'Caricamento',
  'stage.preprocess': 'Preelaborazione',
  'stage.ocr': 'OCR in corso',
  'stage.parse': 'Estrazione campi',
  'stage.validate': 'Validazione',
  'stage.done': 'Fatto',
  'export.empty': 'Nessuna ricevuta pronta',
  'export.one': 'Esporta 1 ricevuta',
  'export.many': 'Esporta tutte ({n} ricevute)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Scarica tutte le ricevute come ZIP: ogni immagine come negozio_Mese_AA.jpg (raggruppate per negozio) più una cartella di lavoro con un foglio di riepilogo e i dettagli per ricevuta',
  'export.billCsv': 'CSV fatture',
  'export.billCsvTooltip':
    'Scarica tutte le ricevute nel formato di importazione fatture: una riga per voce, date GG/MM/AAAA, colonne obbligatorie * invariate',
  'taxRate.label': 'Aliquota IVA',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'Aliquota IVA di questa ricevuta. Lascia vuoto per ricavarla dall’imposta rilevata. Usata come *LineTaxCode nel CSV fatture.',
  'taxRate.applyAll': 'Applica a tutte',
  'taxRate.applyAllTooltip': 'Copia questa aliquota IVA su tutte le altre ricevute del lotto',
  'export.regionLabel': 'Opzioni di esportazione',
  'batch.processing': 'Elaborazione — {done} di {total} pronte',
  'batch.allDone.one': '1 ricevuta pronta',
  'batch.allDone.many': 'Tutte le {total} ricevute sono pronte',
  'batch.allDoneWithErrors.one': '1 ricevuta pronta ({errors} errore)',
  'batch.allDoneWithErrors.many':
    'Tutte le {total} ricevute sono pronte ({errors} errori)',
  'batch.grandTotal': 'Totale generale',
  'batch.partial': '{done} di {total} pronte',
  'receipt.heading': 'Ricevuta',
  'receipt.lineItems': 'Voci',
  'receipt.empty': 'Trascina una ricevuta per iniziare.',
  'receipt.noItems': 'Nessuna voce rilevata.',
  'receipt.fields.vendor': 'Esercente',
  'receipt.fields.date': 'Data',
  'receipt.fields.currency': 'Valuta',
  'receipt.fields.subtotal': 'Subtotale',
  'receipt.fields.tax': 'IVA',
  'receipt.fields.tip': 'Mancia',
  'receipt.fields.total': 'Totale',
  'receipt.fields.payment': 'Pagamento',
  'receipt.cols.item': 'Voce',
  'receipt.cols.qty': 'Qtà',
  'receipt.cols.unitPrice': 'Prezzo unit.',
  'receipt.cols.total': 'Totale',
  'receipt.addItem': 'Aggiungi voce',
  'receipt.removeItem': 'Rimuovi voce, riga {n}',
  'receipt.dragHandle': 'Trascina per riordinare',
  'receipt.dragHandleAria': 'Trascina per riordinare, riga {n}',
  'crop.title': 'Ritaglio manuale',
  'crop.description':
    'Tocca i quattro angoli dello scontrino: prima quello in basso a sinistra, poi in senso orario. Trascina un punto per regolarlo, poi premi Applica.',
  'crop.cancel': 'Annulla',
  'crop.apply': 'Applica ritaglio',
  'crop.close': 'Chiudi',
  'crop.cornerAria': 'Angolo {n}',
  'crop.tapBottomLeft': 'Tocca l’angolo in basso a sinistra dello scontrino',
  'crop.tapTopLeft': 'Ora l’angolo in alto a sinistra',
  'crop.tapTopRight': 'Ora l’angolo in alto a destra',
  'crop.tapBottomRight': 'Infine l’angolo in basso a destra',
  'crop.reset': 'Ricomincia',
  'confidence.high': 'alta',
  'confidence.medium': 'media',
  'confidence.low': 'bassa',
  'theme.toLight': 'Passa al tema chiaro',
  'theme.toDark': 'Passa al tema scuro',
  'debug.show': 'Mostra info di debug',
  'persistence.label': 'Conserva il lotto su questo dispositivo',
  'persistence.tooltip':
    'Salva le ricevute elaborate in questo browser perché sopravvivano a un ricaricamento. Memorizzato solo localmente — nulla esce dal dispositivo.',
  'excludeTax.label': 'Escludi IVA dalle esportazioni',
  'excludeTax.tooltip':
    'Se attivato, le informazioni IVA sono escluse dalle esportazioni CSV / Excel / ZIP. Utile nelle giurisdizioni in cui esportare dati fiscali è rischioso. Anche il subtotale viene nascosto perché permetterebbe di calcolare l’IVA (totale − subtotale). La mancia è indipendente e rimane.',
  'consent.message':
    'I tuoi scontrini non lasciano mai il tuo dispositivo. Usiamo cookie per analytics e pubblicità — accetta per supportare il sito, oppure rifiuta per saltarli.',
  'consent.accept': 'Accetta',
  'consent.reject': 'Rifiuta',
  'consent.privacy': 'Informativa sulla privacy',
  'consent.preferences': 'Preferenze cookie',
  'donate.title': 'Sostieni Receipt Ripper',
  'donate.body': 'Gratuito, finanziato dalla pubblicità, e le tue ricevute non lasciano mai il tuo dispositivo. Le donazioni aiutano a mantenerlo così.',
  'donate.cta': 'Dona con Stripe',
  'donate.skip': 'Salta',
  'donate.countdown': 'Il download parte tra {seconds}s',
  'donate.starting': 'Avvio del download…',
  'validation.totals.title':
    'Voci + IVA + mancia non corrispondono al totale',
  'validation.totals.body':
    'Voci + IVA + mancia = {expected}, ma il totale indica {actual} (differenza {gap}). Controlla i totali delle voci.',
  'summary.title': 'Riepilogo lotto',
  'summary.byCategory': 'Per categoria',
  'summary.topVendors': 'Esercenti principali',
  'summary.byMonth': 'Per mese',
  'tags.label': 'Etichette',
  'tags.placeholder': 'Aggiungi etichetta…',
  'tags.removeAria': 'Rimuovi etichetta {tag}',
  'notes.label': 'Note',
  'notes.placeholder': 'Note su questa ricevuta…',
  'category.label': 'Categoria',
  'category.placeholder': 'Senza categoria',
  'category.autoBadge': 'auto · {pct}%',
  'category.autoTooltip':
    'Rilevata automaticamente da esercente + voci ({pct}% di sicurezza). Scegli una categoria per confermare o modificare.',
  'category.food': 'Cibo',
  'category.transport': 'Trasporti',
  'category.office': 'Ufficio',
  'category.healthcare': 'Salute',
  'category.travel': 'Viaggi',
  'category.utilities': 'Utenze',
  'category.entertainment': 'Svago',
  'category.subscriptions': 'Abbonamenti',
  'category.personal': 'Personale',
  'category.other': 'Altro',
};

const por: Strings = {
  'tagline.onDevice': 'Os seus recibos nunca saem do seu dispositivo',
  'footer.dataPrivacy': 'O conteúdo dos seus recibos nunca sai deste dispositivo.',
  'footer.licenses': 'Licenças de código aberto',
  'footer.about': 'Sobre',
  'footer.faq': 'FAQ',
  'footer.guides': 'Guias',
  'footer.contact': 'Contacto',
  'footer.privacy': 'Privacidade',
  'footer.terms': 'Termos',
  'nav.home': 'Início',
  'language.label': 'Interface',
  'language.receiptLabel': 'Recibos',
  'receiptLang.title': 'Em que idioma estão os seus recibos?',
  'receiptLang.body': 'Isto seleciona o modelo de OCR que lê o seu recibo, não o idioma da interface.',
  'receiptLang.confirmSame': 'Sim, {language}',
  'receiptLang.chooseOther': 'Não, outro idioma',
  'receiptLang.start': 'Começar a digitalizar',
  'receiptLang.cardLabel': 'Idioma do recibo',
  'language.firstUseDownload':
    'A primeira utilização de um novo idioma transfere cerca de 12 MB.',
  'dropzone.dropHere': 'Largue os recibos aqui',
  'dropzone.dropToUpload': 'Largar para carregar',
  'dropzone.subtitle': 'um ou vários — ou cole da área de transferência, ou',
  'dropzone.chooseFile': 'Escolher ficheiro',
  'dropzone.chooseFileAria': 'Escolher ficheiros de recibos',
  'dropzone.takePhoto': 'Tirar uma foto',
  'dropzone.takePhotoAria': 'Capturar um recibo com a câmara',
  'dropzone.accepted': 'Aceites: {extensions}',
  'dropzone.unsupported.one': 'Tipo de ficheiro não suportado. Aceites: {accepted}',
  'dropzone.unsupported.many':
    'Tipos de ficheiro não suportados. Aceites: {accepted}',
  'dropzone.skipped.one': 'Ignorado: 1 ficheiro não suportado.',
  'dropzone.skipped.many': 'Ignorados: {n} ficheiros não suportados.',
  'dropzone.regionLabel': 'Zona de largada de recibos',
  'scan.another': 'Digitalizar outro',
  'sessions.empty': 'Largue um ou mais recibos para começar.',
  'sessions.search.placeholder': 'Pesquisar vendedor, linha ou ficheiro',
  'sessions.search.empty': 'Nenhum recibo corresponde à pesquisa.',
  'sessions.count.partial.one': '1 de {total}',
  'sessions.count.partial.many': '{n} de {total}',
  'sessions.trySample': 'Experimentar com um recibo de exemplo',
  'sessions.count.one': '1 recibo',
  'sessions.count.many': '{n} recibos',
  'sessions.clearDone': 'Limpar concluídos ({n})',
  'sessions.clearDoneTooltip':
    'Remover da lista todos os recibos concluídos (sucesso ou erro)',
  'sessions.clearAll': 'Limpar tudo',
  'sessions.clearAllTooltip.inFlight':
    'Cancelar o trabalho em curso e remover todos os recibos',
  'sessions.clearAllTooltip.idle': 'Remover todos os recibos da lista',
  'sessions.removeFromList': 'Remover da lista',
  'sessions.removeAriaLabel': 'Remover {filename} da lista',
  'sessions.lowQuality.title':
    'A qualidade da foto é demasiado baixa para ler este recibo de forma fiável.',
  'sessions.lowQuality.body':
    'Para melhores resultados, tire a foto novamente com o recibo plano, bem iluminado, totalmente no enquadramento e sem reflexos ou sombras da mão sobre o papel. Os campos abaixo continuam visíveis mas podem estar incompletos ou errados — edite-os manualmente se necessário.',
  'sessions.recropManually': 'Recortar manualmente',
  'sessions.recropTooltip':
    'Ajustar manualmente os cantos do recibo e reprocessar',
  'sessions.rotate': 'Rodar 90°',
  'sessions.rotateTooltip':
    'Rodar a imagem do recibo 90° no sentido horário e reprocessar',
  'sessions.duplicate': 'Possível duplicado',
  'sessions.duplicateTooltip':
    'Outro recibo neste lote tem o mesmo vendedor, data e total',
  'sessions.dragHandle': 'Arrastar para reordenar',
  'sessions.dragHandleAria': 'Arrastar para reordenar, {filename}',
  'sessions.previewTooltip': 'Ver imagem digitalizada',
  'sessions.previewAria': 'Ver imagem digitalizada, {filename}',
  'sessions.cropPreview.hint':
    'Pré-visualização do recorte automático — se não estiver correto, recorte manualmente agora ou remova o recibo e tire a foto novamente.',
  'state.queued': 'Em fila',
  'state.done': 'Concluído',
  'state.review': 'Verificar recorte',
  'sessions.confirmScan': 'Está bom — digitalizar',
  'state.error': 'Erro',
  'state.lowQuality': 'Qualidade baixa',
  'stage.load': 'A carregar',
  'stage.preprocess': 'A pré-processar',
  'stage.ocr': 'A executar OCR',
  'stage.parse': 'A extrair campos',
  'stage.validate': 'A validar',
  'stage.done': 'Concluído',
  'export.empty': 'Sem recibos prontos',
  'export.one': 'Exportar 1 recibo',
  'export.many': 'Exportar todos ({n} recibos)',
  'export.csv': 'CSV',
  'export.xlsx': 'Excel',
  'export.zip': 'ZIP',
  'export.zipTooltip':
    'Transferir todos os recibos como ZIP: cada imagem como loja_Mes_AA.jpg (agrupadas por loja) mais um livro com folha de resumo e detalhes por recibo',
  'export.billCsv': 'CSV de faturas',
  'export.billCsvTooltip':
    'Transferir todos os recibos no formato de importação de faturas: uma linha por item, datas DD/MM/AAAA, colunas obrigatórias * inalteradas',
  'taxRate.label': 'Taxa de IVA',
  'taxRate.placeholder': 'auto',
  'taxRate.tooltip': 'Percentagem de IVA deste recibo. Deixe vazio para a deduzir do imposto detetado. Usada como *LineTaxCode no CSV de faturas.',
  'taxRate.applyAll': 'Aplicar a todos',
  'taxRate.applyAllTooltip': 'Copiar esta taxa de IVA para todos os outros recibos do lote',
  'export.regionLabel': 'Opções de exportação',
  'batch.processing': 'Em processamento — {done} de {total} prontos',
  'batch.allDone.one': '1 recibo concluído',
  'batch.allDone.many': 'Todos os {total} recibos concluídos',
  'batch.allDoneWithErrors.one': '1 recibo concluído ({errors} erro)',
  'batch.allDoneWithErrors.many':
    'Todos os {total} recibos concluídos ({errors} erros)',
  'batch.grandTotal': 'Total geral',
  'batch.partial': '{done} de {total} prontos',
  'receipt.heading': 'Recibo',
  'receipt.lineItems': 'Linhas',
  'receipt.empty': 'Largue um recibo para começar.',
  'receipt.noItems': 'Não foram detetadas linhas.',
  'receipt.fields.vendor': 'Vendedor',
  'receipt.fields.date': 'Data',
  'receipt.fields.currency': 'Moeda',
  'receipt.fields.subtotal': 'Subtotal',
  'receipt.fields.tax': 'IVA',
  'receipt.fields.tip': 'Gorjeta',
  'receipt.fields.total': 'Total',
  'receipt.fields.payment': 'Pagamento',
  'receipt.cols.item': 'Artigo',
  'receipt.cols.qty': 'Qtd.',
  'receipt.cols.unitPrice': 'Preço unit.',
  'receipt.cols.total': 'Total',
  'receipt.addItem': 'Adicionar linha',
  'receipt.removeItem': 'Remover artigo, linha {n}',
  'receipt.dragHandle': 'Arrastar para reordenar',
  'receipt.dragHandleAria': 'Arrastar para reordenar, linha {n}',
  'crop.title': 'Recorte manual',
  'crop.description':
    'Toque nos quatro cantos do recibo — primeiro o inferior esquerdo, depois no sentido horário. Arraste um ponto para afinar e depois pressione Aplicar.',
  'crop.cancel': 'Cancelar',
  'crop.apply': 'Aplicar recorte',
  'crop.close': 'Fechar',
  'crop.cornerAria': 'Canto {n}',
  'crop.tapBottomLeft': 'Toque no canto inferior esquerdo do recibo',
  'crop.tapTopLeft': 'Agora o canto superior esquerdo',
  'crop.tapTopRight': 'Agora o canto superior direito',
  'crop.tapBottomRight': 'Por fim, o canto inferior direito',
  'crop.reset': 'Recomeçar',
  'confidence.high': 'alta',
  'confidence.medium': 'média',
  'confidence.low': 'baixa',
  'theme.toLight': 'Mudar para tema claro',
  'theme.toDark': 'Mudar para tema escuro',
  'debug.show': 'Mostrar info de depuração',
  'persistence.label': 'Manter o lote neste dispositivo',
  'persistence.tooltip':
    'Guarda os recibos processados neste navegador para que sobrevivam a um recarregamento. Armazenado apenas localmente — nada sai do dispositivo.',
  'excludeTax.label': 'Excluir IVA das exportações',
  'excludeTax.tooltip':
    'Quando ativado, as informações de IVA ficam de fora das exportações CSV / Excel / ZIP. Útil em jurisdições onde exportar dados fiscais é arriscado. O subtotal também fica oculto porque permitiria calcular o IVA (total − subtotal). A gorjeta é independente e permanece.',
  'consent.message':
    'Os seus recibos nunca saem do seu dispositivo. Usamos cookies para analytics e publicidade — aceite para apoiar o site, ou rejeite para ignorá-las.',
  'consent.accept': 'Aceitar',
  'consent.reject': 'Rejeitar',
  'consent.privacy': 'Política de privacidade',
  'consent.preferences': 'Preferências de cookies',
  'donate.title': 'Apoiar o Receipt Ripper',
  'donate.body': 'Gratuito, financiado por anúncios, e os seus recibos nunca saem do seu dispositivo. Doações ajudam a manter assim.',
  'donate.cta': 'Doar via Stripe',
  'donate.skip': 'Saltar',
  'donate.countdown': 'O seu download começa em {seconds}s',
  'donate.starting': 'A iniciar o seu download…',
  'validation.totals.title':
    'Linhas + IVA + gorjeta não correspondem ao total',
  'validation.totals.body':
    'Linhas + IVA + gorjeta = {expected}, mas o total indica {actual} (diferença {gap}). Verifica os totais das linhas.',
  'summary.title': 'Resumo do lote',
  'summary.byCategory': 'Por categoria',
  'summary.topVendors': 'Principais vendedores',
  'summary.byMonth': 'Por mês',
  'tags.label': 'Etiquetas',
  'tags.placeholder': 'Adicionar etiqueta…',
  'tags.removeAria': 'Remover etiqueta {tag}',
  'notes.label': 'Notas',
  'notes.placeholder': 'Notas sobre este recibo…',
  'category.label': 'Categoria',
  'category.placeholder': 'Sem categoria',
  'category.autoBadge': 'auto · {pct}%',
  'category.autoTooltip':
    'Detetada automaticamente a partir do vendedor + linhas ({pct}% de confiança). Escolhe uma categoria para confirmar ou substituir.',
  'category.food': 'Alimentação',
  'category.transport': 'Transporte',
  'category.office': 'Escritório',
  'category.healthcare': 'Saúde',
  'category.travel': 'Viagens',
  'category.utilities': 'Serviços',
  'category.entertainment': 'Lazer',
  'category.subscriptions': 'Subscrições',
  'category.personal': 'Pessoal',
  'category.other': 'Outros',
};

const TRANSLATIONS: Record<UiLangCode, Strings> = {
  eng,
  spa,
  fra,
  deu,
  nld,
  ita,
  por,
};

// Map of 2-letter ISO 639-1 prefixes (which navigator.language reports
// as the leading bit of e.g. "es-ES", "fr-CA") to our internal Tesseract
// 3-letter codes. Anything outside the table falls back to English.
const BROWSER_LANG_MAP: Record<string, UiLangCode> = {
  en: 'eng',
  es: 'spa',
  fr: 'fra',
  de: 'deu',
  nl: 'nld',
  it: 'ita',
  pt: 'por',
};

/**
 * Pick the initial UI language for first-time visitors. Reads
 * navigator.language ("es-ES", "fr-CA", "en"), maps the 2-letter prefix
 * to one of our supported codes, falls back to English. The caller
 * (App.tsx) layers localStorage on top — a remembered choice always
 * wins over the browser-locale detection.
 */
export function detectInitialLang(): UiLangCode {
  try {
    const navLang = navigator.language || 'en';
    const prefix = navLang.toLowerCase().slice(0, 2);
    return BROWSER_LANG_MAP[prefix] ?? DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

/**
 * Look up a translation. Falls back to English for any missing key,
 * which shouldn't happen because `Strings` is a structural type and
 * every dictionary above has to satisfy it — but the fallback exists
 * so a future stale dict can't blank-out the UI.
 */
export function t(
  lang: UiLangCode,
  key: StringKey,
  params?: Record<string, string | number>,
): string {
  // The fallback is `eng` (always present in TRANSLATIONS); narrow via
  // `as Strings` since TS can't see that DEFAULT_LANG indexes a known key.
  const dict: Strings = TRANSLATIONS[lang] ?? eng;
  const raw: string = dict[key] ?? eng[key];
  return interpolate(raw, params);
}

function interpolate(
  str: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (match, key) => {
    const v = params[key];
    return v === undefined ? match : String(v);
  });
}

/**
 * Pick the singular/plural form for English-style "1 vs not 1"
 * pluralisation. All seven of our currently-shipped languages follow
 * this rule. If we ever add Russian/Polish/Arabic we'll lift to ICU.
 */
export function tn(
  lang: UiLangCode,
  count: number,
  singular: StringKey,
  plural: StringKey,
  params?: Record<string, string | number>,
): string {
  const key = count === 1 ? singular : plural;
  return t(lang, key, { ...params, n: count });
}
